import { useQuery, UseSuspenseQueryResult } from "@tanstack/react-query";
import client, {
  forBuilding,
  moreMemoryApiClient,
  throwCaughtError,
} from "../client";
import {
  AddUserType,
  MinimalUser,
  MunuUserData,
  UpdateConsent,
  User,
  UserAuthMetadata,
} from "@/types";
import { getRecaptchaToken } from "@app-components";
import { downloadBlobAsXlsx, downloadFile, env, getBuildingId } from "@/lib";
import { emptyOrgNrsMaps } from "@/lib";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

/**
 * Fetches a user, propagating failures. Prefer this over `userOptions` when the
 * caller needs to tell a failed fetch apart from a missing user.
 */
export const userFn = async (
  userId?: string,
  buildingTenantId?: string,
  buildingId?: string,
) => {
  const response = await client.get(
    `/getUser?companyId=${buildingTenantId}&field=userId&value=${userId}`,
    forBuilding(buildingId),
  );
  return response.data.user as User | undefined;
};

/**
 * The logged-in user. Separate from `userOptions` because this one must throw:
 * react-query then keeps the last good user when a refetch fails, instead of
 * `userOptions` overwriting it with undefined and leaving every consumer of
 * `useLoggedInUser` dereferencing nothing.
 *
 * The key extends `userOptions`' key rather than replacing it, so the many
 * existing `invalidateQueries([buildingId, "user", userId])` calls still reach
 * this entry by prefix match. A disjoint key would silently leave the
 * logged-in user stale after e.g. phone validation.
 */
export const loggedInUserOptions = (userId: string) => ({
  queryKey: ["user", userId, "logged-in"],
  queryFn: () => userFn(userId),
  staleTime: 30 * 1000,
});

export const useLoggedInUserQuery = (userId: string) =>
  useBuildingSuspenseQuery(loggedInUserOptions(userId));

export const userOptions = (userId?: string, buildingTenantId?: string) => ({
  queryKey: ["user", userId],
  queryFn: async () => {
    try {
      return await userFn(userId, buildingTenantId);
    } catch (error) {
      console.error(error);
      return undefined;
    }
  },
  enabled: !!userId,
});

export const useUser = (
  userId: string,
  buildingTenantId?: string,
): UseSuspenseQueryResult<User> => {
  return useBuildingSuspenseQuery(userOptions(userId, buildingTenantId));
};

export const useUserByEmailOptions = (
  email?: string,
  buildingTenantId?: string,
) => ({
  queryKey: ["user-by-email", email],
  queryFn: async (): Promise<User | null> => {
    try {
      if (!email) throw new Error("email is required");
      const response = await client.get(
        `/getUser?buildingTenantId=${buildingTenantId}&field=email&value=${encodeURIComponent(
          email,
        )}`,
      );
      return response.data.user as User;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  enabled: !!email,
});

export const userMetadataOptions = (
  userIds: string | string[],
  buildingTenantId?: string,
) => ({
  queryKey: ["user-metadata", userIds],
  queryFn: async (): Promise<UserAuthMetadata[] | UserAuthMetadata> => {
    try {
      if (typeof userIds === "string") {
        // Single user mode
        const response = await client.post(`/getSingleCreatedAtAndLastLogin`, {
          userId: userIds,
        });
        return response.data;
      } else {
        // Multi user mode
        if (!buildingTenantId) throw new Error("buildingTenantId is required");
        const response = await client.post(`/getCreatedAtAndLastLogin`, {
          buildingTenantId,
          userIds,
        });
        if (!Array.isArray(response.data)) {
          return [response.data];
        } else {
          return response.data;
        }
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  },
});

export const buildingUsersOptions = {
  queryKey: ["building-users"],
  queryFn: async () => {
    try {
      const response = await client.get("/getBuildingUsers");
      return (response.data?.users ?? []) as Partial<User>[];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  staleTime: 1000 * 60 * 60 * 24, // 24 hours
  refetchOnWindowFocus: false,
};

export const checkUserLogin = async (email: string) => {
  const response = await fetch(
    `${env("VITE_API_BASE")}/api/getUserLogin?email=${email}`,
    {
      method: "GET",
      headers: {
        Building: getBuildingId(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return response;
};

/**
 * Uses raw fetch rather than the axios client: this runs while nobody is signed in,
 * and the client interceptor throws when there is no current user.
 */
export const logSsoLoginFailure = async (
  code: string,
  typedEmail: string | null,
  details: Record<string, string>,
) => {
  try {
    await fetch(`${env("VITE_API_BASE")}/api/logSsoLoginFailure`, {
      method: "POST",
      body: JSON.stringify({ code, typedEmail, details }),
      headers: {
        Building: getBuildingId(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  } catch (error) {
    console.error("Failed to log SSO login failure:", error);
  }
};

export const loginWithOTP = async (otp: string, email: string) => {
  const recaptchaToken = await getRecaptchaToken("otp_verify");
  const response = await fetch(`${env("VITE_API_BASE")}/api/loginWithOtpCode`, {
    method: "POST",
    body: JSON.stringify({ otp, email, recaptchaToken }),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  const json = await response.json();
  return { ...json, status: response.status };
};

export const assignMicrosoftProvider = async (email: string, uid: string) => {
  const response = await fetch(
    `${env("VITE_API_BASE")}/api/assignMicrosoftProvider`,
    {
      method: "POST",
      body: JSON.stringify({
        email,
        uid,
      }),
      headers: {
        Building: getBuildingId(),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return response;
};

export const companyUsersOptions = (buildingTenantId?: string) => ({
  queryKey: ["users", buildingTenantId],
  queryFn: async (): Promise<User[] | undefined> => {
    try {
      if (!buildingTenantId) throw new Error("buildingTenantId is required");
      const response = await moreMemoryApiClient.get(
        `/getUsersByBuildingTenantId?buildingTenantId=${buildingTenantId}`,
      );
      return response.data.users;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useCompanyUsers = (
  buildingTenantId?: string,
): UseSuspenseQueryResult<User[]> => {
  return useBuildingSuspenseQuery(companyUsersOptions(buildingTenantId));
};

export const useUsersOrgNr = (
  userIds: string[] | undefined = [],
  enabled?: boolean,
) => {
  return useBuildingQuery({
    queryKey: ["users-org-nr", userIds],
    queryFn: async (): Promise<{
      usersOrgNrMap: Map<string, string>;
      companiesOrgNrMap: Map<string, string>;
      subdivisionsOrgNrMap: Map<string, string>;
    }> => {
      if (userIds.length === 0) return emptyOrgNrsMaps;
      try {
        const response = await client.post(`/getUsersOrgNr`, { userIds });
        const { users, companies, subdivisions } = response.data as {
          users: [string, string][];
          companies: [string, string][];
          subdivisions: [string, string][];
        };
        return {
          usersOrgNrMap: new Map(users),
          companiesOrgNrMap: new Map(companies),
          subdivisionsOrgNrMap: new Map(subdivisions),
        };
      } catch (error) {
        throwCaughtError(error);
        return emptyOrgNrsMaps;
      }
    },
    enabled,
  });
};

export const getExportedCompanyUsers = async (
  companyId: string,
  companyName?: string,
  options?: { excel?: boolean },
): Promise<File | undefined> => {
  try {
    const response = await client.get(
      `/exportUsersCsv?buildingTenantId=${companyId}`,
      { responseType: "blob" },
    );
    const fileType = options?.excel ? "xlsx" : "csv";
    const filename = `${(companyName || companyId).split(" ").join("_")}_users_export.${fileType}`;
    if (options?.excel) {
      await downloadBlobAsXlsx(response.data, filename);
      return;
    }
    const blob = new Blob([response.data], { type: "text/csv" });
    downloadFile(blob, filename);
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const getExportedBuildingUsers = async (
  buildingName: string | undefined,
  options?: { excel?: boolean },
): Promise<File | undefined> => {
  try {
    const response = await client.get("/exportAllUsersCsv", {
      responseType: "blob",
    });
    const fileType = options?.excel ? "xlsx" : "csv";
    const fileName = buildingName
      ? `${buildingName.split(" ").join("_")}_all_users_export.${fileType}`
      : "all_users_export.${fileType}`";

    if (options?.excel) {
      await downloadBlobAsXlsx(response.data, fileName);
      return;
    }
    const blob = new Blob([response.data], { type: "text/csv" });
    downloadFile(blob, fileName);
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const getUser = async (
  field?: string,
  value?: string,
  companyId?: string,
): Promise<User> => {
  const response = await client.get(
    `/getUser?field=${field}&value=${value}` +
      (companyId ? `&companyId=${companyId}` : ""),
  );
  const user = response.data.user;
  if (!user) throw new Error("User not found");
  return response.data.user;
};

export const getUserNamesOptions = (isAdmin: boolean, userIds: string[]) => {
  return {
    queryKey: ["get-user-ids", userIds],
    queryFn: async () => {
      if (!isAdmin) return {};
      try {
        const deduplicated = [...new Set(userIds)];

        const response = await client.post(`/getUserNames`, {
          userIds: deduplicated,
        });
        const userMap: { [key: string]: string } = {};
        response.data.users.forEach(
          (user: { userId: string; userName: string }) => {
            userMap[user.userId] = user.userName;
          },
        );
        return userMap;
      } catch (error) {
        console.log(error);
        return {};
      }
    },
  };
};

export const getUserPhoneNumbersOptions = (
  userIds: string[],
  supplierCompanyId?: string,
  enabled?: boolean,
) => {
  return {
    queryKey: ["get-user-phone-numbers", userIds, supplierCompanyId],
    queryFn: async (): Promise<{ userId: string; phone: string }[]> => {
      try {
        const response = await client.post(`/getUserPhoneNumbers`, {
          userIds,
          supplierCompanyId,
        });
        return response.data.users;
      } catch (error) {
        console.log(error);
        return [];
      }
    },
    enabled:
      supplierCompanyId !== undefined &&
      userIds.length > 0 &&
      enabled !== false,
  };
};

export const useUserPhoneNumbers = (
  userIds: string[],
  supplierCompanyId?: string,
  enabled?: boolean,
) => {
  return useBuildingQuery(
    getUserPhoneNumbersOptions(userIds, supplierCompanyId, enabled),
  );
};

export const getUserByEmail = async (
  email: string,
  companyId?: string,
  includeAdmins?: boolean,
  // Company the caller administers. Only used by the permission guard to
  // authorize company admins; unlike companyId it does not filter results.
  authCompanyId?: string,
): Promise<User[]> => {
  try {
    const response = await client.get(
      `/getUserByEmail?email=${encodeURIComponent(email.toLowerCase())}` +
        (companyId ? `&companyId=${companyId}` : "") +
        (includeAdmins ? `&includeAdmins=${includeAdmins}` : "") +
        (authCompanyId ? `&buildingTenantId=${authCompanyId}` : ""),
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const createUsers = async (
  users: AddUserType[],
  invitingAdminName: string,
  invitingAdminPrimaryCompanyName: string,
  buildingTenantId: string,
) => {
  return await moreMemoryApiClient.post("/createUser", {
    users,
    invitingAdminName,
    invitingAdminPrimaryCompanyName,
    buildingTenantId,
  });
};

export const updateUser = async (
  userId: string,
  updateData: Partial<User>,
  oldEmail?: string,
  buildingTenantId?: string,
  imageUrl?: string,
) => {
  return await client.patch("/updateUser", {
    userId,
    updateData,
    oldEmail,
    buildingTenantId,
    imageUrl,
  });
};

export const reactivateDisabledUser = async (
  userId: string,
  buildingTenantId: string,
) => {
  return await client.patch("/reactivateDisabledUser", {
    userId,
    buildingTenantId,
  });
};

export const updateUserResignationDate = async (
  userId: string,
  buildingTenantId: string,
  resignationDate?: Date | null,
) => {
  return await client.patch("/updateUserResignationDate", {
    userId,
    buildingTenantId,
    resignationDate,
  });
};

export const updateConsent = async (
  userId: string,
  consents: UpdateConsent[],
) => {
  return await client.patch("/updateConsent", {
    userId,
    consents,
  });
};

export const deleteUser = async (
  userId: string,
  email: string,
  buildingTenantId: string,
  firstName: string,
  lastName: string,
) => {
  return await client.delete("/deleteUser", {
    data: {
      userId,
      email,
      buildingTenantId,
      firstName,
      lastName,
    },
  });
};

export const sendLoginOTP = async (
  email: string,
  mode: "sms" | "email",
): Promise<{
  status: number;
  message: string;
  mode: "sms" | "email";
  hasPhone: boolean;
  code?: string;
  retryAfterSeconds?: number;
}> => {
  const recaptchaToken = await getRecaptchaToken("otp_send");
  const body = JSON.stringify({ email, mode, recaptchaToken });
  const response = await fetch(`${env("VITE_API_BASE")}/api/sendOtpLoginCode`, {
    method: "POST",
    body,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  const json = await response.json();
  return { ...json, status: response.status };
};

export type ExistingUserCheckResult = {
  userExist: boolean;
  alreadyInCurrentBuilding: boolean;
  sameCompany: boolean;
  existingUserData?: {
    firstName: string;
    lastName: string;
    phone?: string;
    jobTitle?: string;
    buildingTenantId: string;
  };
};

export const checkExistingUser = async (
  email: string,
  buildingTenantId: string,
): Promise<ExistingUserCheckResult> => {
  const encodedEmail = encodeURIComponent(email);
  try {
    const response = await client.get(
      `/checkIfUserExists?email=${encodedEmail}&buildingTenantId=${buildingTenantId}`,
    );
    return response.data as ExistingUserCheckResult;
  } catch (error) {
    throwCaughtError(error);
    return {
      userExist: false,
      alreadyInCurrentBuilding: false,
      sameCompany: false,
    };
  }
};

export type ExistingEmailsCheckResult = {
  emailsInCurrentBuilding: string[];
  emailsInDifferentCompany: string[];
};

export const checkExistingEmails = async (
  newEmails: string[],
  buildingTenantId: string,
): Promise<ExistingEmailsCheckResult> => {
  try {
    const response = await client.post("/checkExistingEmails", {
      newEmails,
      buildingTenantId,
    });
    return {
      emailsInCurrentBuilding:
        response.data.emailsInCurrentBuilding ??
        response.data.repeatingEmails ??
        [],
      emailsInDifferentCompany: response.data.emailsInDifferentCompany ?? [],
    };
  } catch (error) {
    throwCaughtError(error);
    return { emailsInCurrentBuilding: [], emailsInDifferentCompany: [] };
  }
};

export const munuCompanyUsersOptions = (buildingTenantId: string) => ({
  queryKey: ["munu-company-users", buildingTenantId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getMunuCompanyUsers?buildingTenantId=${buildingTenantId}`,
      );
      return response.data as MunuUserData[];
    } catch (error) {
      throwCaughtError(error);
      return [];
    }
  },
});

export const useMunuCompanyUsers = (buildingTenantId: string) => {
  return useBuildingSuspenseQuery(munuCompanyUsersOptions(buildingTenantId));
};

export const createMunuUser = async (body: {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  userId: string;
  buildingTenantId: string;
  accessCard: string;
  dealId: number;
  subdivisionId?: string;
}) => {
  const response = await client.post("/createMunuUser", body);
  return response;
};

export const updateMunuUser = async (body: {
  userId: string;
  phone: string;
  email: string;
  lastName: string;
  firstName: string;
  accessCard: string;
  buildingTenantId: string;
  subdivisionId?: string;
}) => {
  try {
    const response = await client.patch("/updateMunuUser", body);
    return response;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const deactivateMunuUser = async (body: {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  munuUserId: number;
  buildingTenantId: string;
  subdivisionId?: string;
}) => {
  const response = await client.post("/deactivateMunuUser", body);
  return response;
};

export const reactivateMunuUser = async (body: {
  munuUserId: number;
  buildingTenantId: string;
  subdivisionId?: string;
}) => {
  const response = await client.post("/reactivateMunuUser", body);
  return response;
};

export const getUsersByIdsOptions = (
  selectedUserIds: string[],
  enabled = true,
) => {
  return {
    queryKey: ["usersByIds", selectedUserIds],
    queryFn: async () => {
      const response = await client.post<MinimalUser[]>(
        `/getMultipleUsersByIds`,
        { userIds: selectedUserIds },
      );
      return response.data;
    },
    enabled: enabled && selectedUserIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  };
};

export const makeUserBuildingAdmin = async (
  userId: string,
  buildingId: string,
) => {
  const response = await client.post(`/addUserAndMakeBuildingAdmin`, {
    userId,
    buildingId,
  });
  return response.data;
};

export const getUserBuildings = async (userId: string) => {
  const response = await client.get(`/getUserBuildings?userId=${userId}`);
  return response.data;
};

export const userBuildingsFn = async (userId: string) => {
  const response = await client.get(`/getUserBuildings?userId=${userId}`);
  return response.data.buildings as {
    buildingId: string;
    buildingName: string;
  }[];
};

export const userBuildingsOptions = (userId: string) => ({
  queryKey: ["userBuildings", userId],
  queryFn: async () => {
    try {
      return await userBuildingsFn(userId);
    } catch (error) {
      if (!window.localStorage.getItem("BUILDING")) {
        window.localStorage.setItem(
          "BUILDING",
          import.meta.env.VITE_REAL_ESTATE,
        );
      }
      console.error(error);
      return [];
    }
  },
});

export const useUserBuildings = (userId: string) => {
  return useBuildingSuspenseQuery(userBuildingsOptions(userId));
};

export const getUserByEmailInAllBuildings = async (email: string) => {
  try {
    if (email.length === 0) return null;
    const response = await client.get(
      `/getUserByEmailInAllBuildings?email=${encodeURIComponent(email)}`,
    );
    return response.data.user as User & {
      buildings: {
        building: string;
        companyId: string;
      }[];
    };
  } catch (error) {
    console.error(error);
    return null;
  }
};
export type UserAllBuildingsMatch = {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  sharryUserId?: string;
  userActive: boolean;
  lastActiveDate: number | null;
  buildings: {
    building: string;
    companyId: string;
    companyName: string;
    isAdmin: boolean;
    isAccessAdmin: boolean;
    integratedWithAccessControlSystem: boolean;
    integratedWithWelcomeFood: boolean;
  }[];
};

export const searchUsersInAllBuildings = async (
  query: string,
): Promise<UserAllBuildingsMatch[]> => {
  if (query.trim().length === 0) return [];
  const response = await client.get("/searchUsersInAllBuildings", {
    params: { query: query.trim() },
  });
  return response.data.users as UserAllBuildingsMatch[];
};
export const getUserByIdInAllBuildings = async (id: string) => {
  try {
    if (id.length === 0) return null;
    const response = await client.get(
      `/getUserByIdInAllBuildings?id=${encodeURIComponent(id)}`,
    );
    return response.data.user as User;
  } catch (error) {
    console.error(error);
    return null;
  }
};
export const userByEmailInAllBuildingsOptions = (email: string) => ({
  queryKey: ["userByEmailInAllBuildings", email],
  queryFn: async () => getUserByEmailInAllBuildings(email),
});
export const userByIdInAllBuildingsOptions = (id: string) => ({
  queryKey: ["userByIdInAllBuildings", id],
  queryFn: async () => getUserByIdInAllBuildings(id),
});

export const useUserByEmailInAllBuildings = (email: string) => {
  return useQuery(userByEmailInAllBuildingsOptions(email));
};
export const useUserByIdInAllBuildings = (id: string) => {
  return useQuery(userByIdInAllBuildingsOptions(id));
};
